import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  FileText,
  LayoutDashboard,
  MessageSquare,
  Search,
  Table2,
  Workflow,
  X,
} from 'lucide-react';
import {Dialog, DialogContent} from '@/matic/components/ui/dialog';
import {cn} from '@/matic/lib/utils/cn';
import {useOttoSessions} from '@/matic/features/agents/otto/hooks/useOttoSessions';
import {useLibraryRail} from '@/matic/features/relay/hooks/useLibraryRail';
import {useThreadSearch} from '@/matic/features/relay/hooks/useThreadSearch';
import {useAllThreads} from '@/matic/features/relay/hooks/useAllThreads';
import {
  encodeScopeParam,
  parseScopeParam,
} from '@/matic/features/relay/lib/scope-utils';
import {
  resolveViewInstanceLabels,
  type ResolvedViewInstance,
} from '@/matic/features/relay/lib/view-instance-labels';
import {
  useViewDefinitions,
  useViewInstances,
} from '@/matic/features/agents/otto/views/hooks/useView';
import type {Thread} from '@/matic/features/relay/types/relay-types';
import type {OttoSessionContext} from '@/matic/features/agents/otto/types/otto-models';
import {
  resolveSessionContextDetails,
  scopeItemsFromContext,
} from '@/matic/features/relay/lib/otto-session-summary';

type LibraryGlobalSearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type SearchGroupProps = {
  title: string;
  count?: number;
  children: ReactNode;
};

type RecordSearchResult = {
  typeId: string;
  typeName: string;
  recordId: string;
  label: string;
};

const LOCAL_RESULT_LIMIT = 6;
const THREAD_RESULT_LIMIT = 6;

/** DOM-safe id prefix for `aria-activedescendant` roving-focus rows. */
const OPTION_ID_PREFIX = 'library-search-option';
const LISTBOX_ID = 'library-search-listbox';

function optionId(key: string): string {
  return `${OPTION_ID_PREFIX}-${key.replaceAll(/[^\w-]/g, '_')}`;
}

export function LibraryGlobalSearchDialog({
  open,
  onOpenChange,
}: LibraryGlobalSearchDialogProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const rail = useLibraryRail();
  const {sessions, isLoading: sessionsLoading} = useOttoSessions({
    pageType: 'relay',
    enabled: open,
  });
  const allThreadsQuery = useAllThreads(50);
  const threadSearchQuery = useThreadSearch(query);
  // Both tenant-wide and already cached elsewhere on this page (browse hub /
  // home digest), so TanStack dedupes — no extra request for opening search.
  // `useViewDefinitions()` bare = every active Definition plus the built-ins,
  // which is what a global dialog wants.
  const viewInstancesQuery = useViewInstances();
  const viewDefinitionsQuery = useViewDefinitions();

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [open]);

  const q = query.trim().toLowerCase();
  const {types, recordsByType} = rail;
  const threadResults =
    q.length >= 2
      ? (threadSearchQuery.data?.items ?? [])
      : (allThreadsQuery.data?.items ?? []).slice(0, THREAD_RESULT_LIMIT);

  const typeResults = useMemo(() => {
    if (!q) return types.slice(0, 4);
    return types
      .filter((type) => type.name.toLowerCase().includes(q))
      .slice(0, LOCAL_RESULT_LIMIT);
  }, [q, types]);

  const recordResults = useMemo(() => {
    const results: RecordSearchResult[] = [];
    for (const type of types) {
      for (const record of recordsByType[type.id] ?? []) {
        const label = record.display ?? record.code;
        const haystack =
          `${label} ${record.code ?? ''} ${type.name}`.toLowerCase();
        if (!q || haystack.includes(q)) {
          results.push({
            typeId: type.id,
            typeName: type.name,
            recordId: record.id,
            label,
          });
        }

        if (results.length >= LOCAL_RESULT_LIMIT) return results;
      }
    }

    return results;
  }, [q, recordsByType, types]);

  const viewResults = useMemo(() => {
    const resolved = resolveViewInstanceLabels(
      viewInstancesQuery.data ?? [],
      viewDefinitionsQuery.data ?? [],
      types,
      recordsByType
    );
    // Already sorted `generatedAt` desc — on an empty query show the 4 most
    // recent, mirroring `typeResults`' 4-on-empty default.
    if (!q) return resolved.slice(0, 4);
    return resolved
      .filter((instance) =>
        `${instance.scopeLabel} ${instance.definitionName}`
          .toLowerCase()
          .includes(q)
      )
      .slice(0, LOCAL_RESULT_LIMIT);
  }, [
    q,
    recordsByType,
    types,
    viewDefinitionsQuery.data,
    viewInstancesQuery.data,
  ]);

  const conversationResults = useMemo(() => {
    const filtered = q
      ? sessions.filter((session) => {
          const details = resolveSessionContextDetails(
            session.context,
            recordsByType
          );
          const haystack = [
            session.title ?? 'Untitled conversation',
            ...details.anchors.map((chip) => chip.label),
            ...details.context.map((chip) => chip.label),
            ...details.anchors.map((chip) => chip.kindLabel ?? ''),
            ...details.context.map((chip) => chip.kindLabel ?? ''),
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(q);
        })
      : sessions;
    return filtered.slice(0, LOCAL_RESULT_LIMIT);
  }, [q, recordsByType, sessions]);

  const closeAndNavigate = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const openRecord = (typeId: string, recordId: string) => {
    const params = new URLSearchParams();
    params.set('scope', `${typeId}:${recordId}`);
    closeAndNavigate(`/library?${params.toString()}`);
  };

  const openType = (typeId: string) => {
    const params = new URLSearchParams();
    params.set('scope', `${typeId}:*`);
    closeAndNavigate(`/library?${params.toString()}`);
  };

  // Mirrors `LibraryContainer.handleOpenView`'s param contract: scope + the
  // Views tab + the specific Definition, with competing params left unset.
  const openView = (scopeKey: string, definitionId: string) => {
    const params = new URLSearchParams();
    const aggregateTypeId = scopeKey.startsWith('type:')
      ? scopeKey.slice('type:'.length)
      : undefined;
    params.set(
      'scope',
      aggregateTypeId
        ? `${aggregateTypeId}:*`
        : encodeScopeParam(parseScopeParam(scopeKey))
    );
    params.set('tab', 'views');
    params.set('template', definitionId);
    closeAndNavigate(`/library?${params.toString()}`);
  };

  const openConversation = (
    sessionId: string,
    context: OttoSessionContext | undefined
  ) => {
    const params = new URLSearchParams();
    params.set('chat', sessionId);
    const scopeItems = scopeItemsFromContext(context);
    if (scopeItems.length > 0) {
      params.set('scope', encodeScopeParam(scopeItems));
    }

    closeAndNavigate(`/library?${params.toString()}`);
  };

  const openThread = (thread: Thread) => {
    const params = new URLSearchParams();
    params.set('thread', thread.id);
    const scopeItems = thread.associations.map((association) => ({
      typeId: association.objectTypeId,
      // Type-only ("Category") association → the typeId:* Category scope.
      recordId: association.objectRecordId ?? '*',
    }));
    if (scopeItems.length > 0) {
      params.set('scope', encodeScopeParam(scopeItems));
    }

    closeAndNavigate(`/library?${params.toString()}`);
  };

  const hasAnyResults =
    typeResults.length > 0 ||
    recordResults.length > 0 ||
    viewResults.length > 0 ||
    conversationResults.length > 0 ||
    threadResults.length > 0;
  const isLoadingResults =
    rail.typesLoading ||
    rail.recordsLoading ||
    sessionsLoading ||
    threadSearchQuery.isLoading ||
    allThreadsQuery.isLoading;

  // Flattened, render-order list of every result across all five groups —
  // the roving-focus sequence for arrow-key nav + Enter-to-activate. Rebuilt
  // whenever the underlying result sets change; keyed the same way each
  // group's rows are keyed below.
  const flatResults = useMemo(() => {
    const flat: Array<{key: string; onActivate: () => void}> = [];
    for (const result of recordResults) {
      flat.push({
        key: `record:${result.typeId}:${result.recordId}`,
        onActivate() {
          openRecord(result.typeId, result.recordId);
        },
      });
    }

    for (const instance of viewResults) {
      flat.push({
        key: `view:${instance.scopeKey}:${instance.definitionId}`,
        onActivate() {
          openView(instance.scopeKey, instance.definitionId);
        },
      });
    }

    for (const type of typeResults) {
      flat.push({
        key: `type:${type.id}`,
        onActivate() {
          openType(type.id);
        },
      });
    }

    for (const session of conversationResults) {
      flat.push({
        key: `conversation:${session.id}`,
        onActivate() {
          openConversation(session.id, session.context);
        },
      });
    }

    for (const thread of threadResults.slice(0, THREAD_RESULT_LIMIT)) {
      flat.push({
        key: `thread:${thread.id}`,
        onActivate() {
          openThread(thread);
        },
      });
    }

    return flat;
  }, [
    recordResults,
    viewResults,
    typeResults,
    conversationResults,
    threadResults,
  ]);

  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  // Keep the active row valid: default to the first result whenever the
  // active key falls off the (re-filtered) list, including on first results.
  useEffect(() => {
    if (flatResults.some((r) => r.key === activeKey)) return;
    setActiveKey(flatResults[0]?.key);
  }, [flatResults, activeKey]);

  // Keep the active row in view as arrow keys move it — the listbox scrolls
  // independently of the input, so an active row past the fold would
  // otherwise be invisible to a keyboard user.
  useEffect(() => {
    if (!activeKey) return;
    document
      .querySelector(`#${optionId(activeKey)}`)
      ?.scrollIntoView({block: 'nearest'});
  }, [activeKey]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (flatResults.length === 0) return;
      const currentIndex = flatResults.findIndex((r) => r.key === activeKey);
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const next = flatResults[(currentIndex + 1) % flatResults.length];
          setActiveKey(next.key);

          break;
        }

        case 'ArrowUp': {
          event.preventDefault();
          const previousIndex =
            (currentIndex - 1 + flatResults.length) % flatResults.length;
          setActiveKey(flatResults[previousIndex].key);

          break;
        }

        case 'Enter': {
          event.preventDefault();
          const active =
            flatResults.find((r) => r.key === activeKey) ?? flatResults[0];
          active.onActivate();

          break;
        }
        // No default
      }
    },
    [flatResults, activeKey]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl overflow-hidden p-0"
        aria-label="Global search"
      >
        <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-neutral-400" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded={hasAnyResults}
            aria-controls={LISTBOX_ID}
            aria-activedescendant={activeKey ? optionId(activeKey) : undefined}
            aria-autocomplete="list"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Search records, conversations, threads..."
            className="min-w-0 flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
          />
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
            aria-label="Close search"
            className="rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <SearchResultsList
          q={q}
          hasAnyResults={hasAnyResults}
          isLoadingResults={isLoadingResults}
          recordsByType={recordsByType}
          recordResults={recordResults}
          viewResults={viewResults}
          typeResults={typeResults}
          conversationResults={conversationResults}
          sessionsLoading={sessionsLoading}
          threadResults={threadResults}
          threadsLoading={threadSearchQuery.isLoading}
          activeKey={activeKey}
          onOpenRecord={openRecord}
          onOpenView={openView}
          onOpenType={openType}
          onOpenConversation={openConversation}
          onOpenThread={openThread}
        />
      </DialogContent>
    </Dialog>
  );
}

function SearchResultsList({
  q,
  hasAnyResults,
  isLoadingResults,
  recordsByType,
  recordResults,
  viewResults,
  typeResults,
  conversationResults,
  sessionsLoading,
  threadResults,
  threadsLoading,
  activeKey,
  onOpenRecord,
  onOpenView,
  onOpenType,
  onOpenConversation,
  onOpenThread,
}: {
  q: string;
  hasAnyResults: boolean;
  isLoadingResults: boolean;
  recordsByType: ReturnType<typeof useLibraryRail>['recordsByType'];
  recordResults: RecordSearchResult[];
  viewResults: ResolvedViewInstance[];
  typeResults: ReturnType<typeof useLibraryRail>['types'];
  conversationResults: ReturnType<typeof useOttoSessions>['sessions'];
  sessionsLoading: boolean;
  threadResults: Thread[];
  threadsLoading: boolean;
  activeKey: string | undefined;
  onOpenRecord: (typeId: string, recordId: string) => void;
  onOpenView: (scopeKey: string, definitionId: string) => void;
  onOpenType: (typeId: string) => void;
  onOpenConversation: (
    sessionId: string,
    context: OttoSessionContext | undefined
  ) => void;
  onOpenThread: (thread: Thread) => void;
}) {
  return (
    <div
      id={LISTBOX_ID}
      role="listbox"
      aria-label="Search results"
      className="max-h-[60vh] overflow-y-auto px-2 py-2"
    >
      {!hasAnyResults && !isLoadingResults && (
        <div className="px-3 py-8 text-center text-sm text-neutral-500">
          No matches.
        </div>
      )}
      <RecordResults
        results={recordResults}
        activeKey={activeKey}
        onOpenRecord={onOpenRecord}
      />
      <ViewResults
        results={viewResults}
        activeKey={activeKey}
        onOpenView={onOpenView}
      />
      <TypeResults
        results={typeResults}
        activeKey={activeKey}
        onOpenType={onOpenType}
      />
      <ConversationResults
        results={conversationResults}
        recordsByType={recordsByType}
        isLoading={sessionsLoading}
        activeKey={activeKey}
        onOpenConversation={onOpenConversation}
      />
      <ThreadResults
        title={q.length >= 2 ? 'Threads' : 'Recent threads'}
        results={threadResults}
        isLoading={threadsLoading}
        activeKey={activeKey}
        onOpenThread={onOpenThread}
      />
    </div>
  );
}

function RecordResults({
  results,
  activeKey,
  onOpenRecord,
}: {
  results: RecordSearchResult[];
  activeKey: string | undefined;
  onOpenRecord: (typeId: string, recordId: string) => void;
}) {
  if (results.length === 0) return null;
  return (
    <SearchGroup title="Records" count={results.length}>
      {results.map((result) => {
        const key = `record:${result.typeId}:${result.recordId}`;
        return (
          <SearchResultRow
            key={key}
            resultKey={key}
            active={activeKey === key}
            icon={<Table2 className="h-4 w-4" />}
            title={result.label}
            subtitle={result.typeName}
            onClick={() => {
              onOpenRecord(result.typeId, result.recordId);
            }}
          />
        );
      })}
    </SearchGroup>
  );
}

function ViewResults({
  results,
  activeKey,
  onOpenView,
}: {
  results: ResolvedViewInstance[];
  activeKey: string | undefined;
  onOpenView: (scopeKey: string, definitionId: string) => void;
}) {
  if (results.length === 0) return null;
  return (
    <SearchGroup title="Views" count={results.length}>
      {results.map((instance) => {
        const key = `view:${instance.scopeKey}:${instance.definitionId}`;
        return (
          <SearchResultRow
            key={key}
            resultKey={key}
            active={activeKey === key}
            icon={<LayoutDashboard className="h-4 w-4" />}
            title={instance.definitionName}
            subtitle={instance.scopeLabel}
            onClick={() => {
              onOpenView(instance.scopeKey, instance.definitionId);
            }}
          />
        );
      })}
    </SearchGroup>
  );
}

function TypeResults({
  results,
  activeKey,
  onOpenType,
}: {
  results: ReturnType<typeof useLibraryRail>['types'];
  activeKey: string | undefined;
  onOpenType: (typeId: string) => void;
}) {
  if (results.length === 0) return null;
  return (
    <SearchGroup title="Types" count={results.length}>
      {results.map((type) => {
        const key = `type:${type.id}`;
        return (
          <SearchResultRow
            key={key}
            resultKey={key}
            active={activeKey === key}
            icon={<Workflow className="h-4 w-4" />}
            title={type.name}
            subtitle="Category"
            onClick={() => {
              onOpenType(type.id);
            }}
          />
        );
      })}
    </SearchGroup>
  );
}

function ConversationResults({
  results,
  recordsByType,
  isLoading,
  activeKey,
  onOpenConversation,
}: {
  results: ReturnType<typeof useOttoSessions>['sessions'];
  recordsByType: ReturnType<typeof useLibraryRail>['recordsByType'];
  isLoading: boolean;
  activeKey: string | undefined;
  onOpenConversation: (
    sessionId: string,
    context: OttoSessionContext | undefined
  ) => void;
}) {
  if (results.length === 0 && !isLoading) return null;
  return (
    <SearchGroup title="Conversations" count={results.length}>
      {isLoading && results.length === 0 ? (
        <SearchLoadingRows />
      ) : (
        results.map((session) => {
          const key = `conversation:${session.id}`;
          return (
            <SearchResultRow
              key={key}
              resultKey={key}
              active={activeKey === key}
              icon={<MessageSquare className="h-4 w-4" />}
              title={session.title ?? 'Untitled conversation'}
              subtitle={
                resolveSessionContextDetails(session.context, recordsByType)
                  .anchors[0]?.label ?? 'Conversation'
              }
              onClick={() => {
                onOpenConversation(session.id, session.context);
              }}
            />
          );
        })
      )}
    </SearchGroup>
  );
}

function ThreadResults({
  title,
  results,
  isLoading,
  activeKey,
  onOpenThread,
}: {
  title: string;
  results: Thread[];
  isLoading: boolean;
  activeKey: string | undefined;
  onOpenThread: (thread: Thread) => void;
}) {
  if (results.length === 0 && !isLoading) return null;
  return (
    <SearchGroup title={title} count={results.length}>
      {isLoading && results.length === 0 ? (
        <SearchLoadingRows />
      ) : (
        results.slice(0, THREAD_RESULT_LIMIT).map((thread) => {
          const key = `thread:${thread.id}`;
          return (
            <SearchResultRow
              key={key}
              resultKey={key}
              active={activeKey === key}
              icon={<FileText className="h-4 w-4" />}
              title={thread.subject ?? 'No subject'}
              subtitle={threadAssociationSummary(thread)}
              onClick={() => {
                onOpenThread(thread);
              }}
            />
          );
        })
      )}
    </SearchGroup>
  );
}

function threadAssociationSummary(thread: Thread) {
  return thread.associations
    .map(
      (association) =>
        association.objectRecordName ?? association.objectRecordCode
    )
    .filter(Boolean)
    .slice(0, 2)
    .join(' + ');
}

function SearchGroup({title, count, children}: SearchGroupProps) {
  return (
    <section className="py-1">
      <div className="flex items-center gap-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
        <span>{title}</span>
        {count !== undefined && count > 0 && (
          <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-500">
            {count}
          </span>
        )}
      </div>
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

function SearchResultRow({
  resultKey,
  active,
  icon,
  title,
  subtitle,
  onClick,
}: {
  resultKey: string;
  active: boolean;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button
      id={optionId(resultKey)}
      type="button"
      role="option"
      aria-selected={active}
      // Not a real tab stop — the combobox pattern keeps focus on the input
      // and drives these via `aria-activedescendant` + arrow keys; a mouse
      // click still activates the row directly via onClick.
      tabIndex={-1}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-neutral-100',
        active && 'bg-neutral-100'
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-neutral-100 text-neutral-500">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-neutral-900">
          {title}
        </span>
        {subtitle && (
          <span className="block truncate text-xs text-neutral-500">
            {subtitle}
          </span>
        )}
      </span>
    </button>
  );
}

function SearchLoadingRows() {
  return (
    <div className="space-y-2 px-2 py-2">
      {['a', 'b', 'c'].map((key, index) => (
        <div
          key={key}
          className={cn(
            'h-4 animate-pulse rounded bg-neutral-100',
            index === 1 ? 'w-3/4' : 'w-2/3'
          )}
        />
      ))}
    </div>
  );
}
